import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import MARGrid from "@/components/MARGrid";
import {
  Loader2,
  Calendar,
  Pill,
  CheckCircle2,
  XCircle,
  PauseCircle,
} from "lucide-react";

function StatusPill({ status }) {
  const meta = {
    given: { label: "Given", icon: CheckCircle2, cls: "bg-emerald-500/10 text-emerald-700" },
    refused: { label: "Refused", icon: XCircle, cls: "bg-destructive/10 text-destructive" },
    held: { label: "Held", icon: PauseCircle, cls: "bg-amber-500/10 text-amber-700" },
    missed: { label: "Missed", icon: XCircle, cls: "bg-destructive/10 text-destructive" },
  }[status] || { label: status, icon: Pill, cls: "bg-clinical-bg text-clinical-subtext" };
  return (
    <span className={`pill ${meta.cls}`}>
      <meta.icon className="h-3 w-3" /> {meta.label}
    </span>
  );
}

export default function AdminMAR() {
  const [clients, setClients] = useState([]);
  const [clientId, setClientId] = useState("");
  const [dateStr, setDateStr] = useState(new Date().toISOString().slice(0, 10));
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  useEffect(() => {
    api.get("/clients").then(({ data }) => {
      setClients(data);
      if (data.length && !clientId) setClientId(data[0].id);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!clientId) return;
    setLoadingHistory(true);
    const start = new Date(dateStr);
    start.setDate(start.getDate() - 6);
    api
      .get(`/clients/${clientId}/mar/history`, {
        params: { start: start.toISOString().slice(0, 10), end: dateStr },
      })
      .then(({ data }) => setHistory(data))
      .finally(() => setLoadingHistory(false));
  }, [clientId, dateStr]);

  const client = clients.find((c) => c.id === clientId);

  // Group history by date — memoized so React doesn't rebuild the grouping every render
  const groupedHistory = useMemo(() => {
    return history.reduce((acc, e) => {
      (acc[e.scheduled_date] = acc[e.scheduled_date] || []).push(e);
      return acc;
    }, {});
  }, [history]);
  const groupedHistoryEntries = useMemo(
    () => Object.entries(groupedHistory).sort((a, b) => (a[0] < b[0] ? 1 : -1)),
    [groupedHistory]
  );

  return (
    <div className="space-y-6" data-testid="admin-mar-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="label-eyebrow">Compliance</div>
          <h1 className="mt-1 text-3xl font-heading font-semibold tracking-tight">
            Medication Records
          </h1>
          <p className="mt-1 text-sm text-clinical-subtext">
            View scheduled doses and administration history per client.
          </p>
        </div>
      </div>

      <div className="card-surface p-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <label className="label-eyebrow mb-1.5 block">Client</label>
            <select
              value={clientId}
              onChange={(e) => setClientId(e.target.value)}
              className="input-clinical"
              data-testid="mar-client-select"
            >
              {clients.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="label-eyebrow mb-1.5 block">Date</label>
            <input
              type="date"
              value={dateStr}
              onChange={(e) => setDateStr(e.target.value)}
              className="input-clinical"
              data-testid="mar-date-input"
            />
          </div>
        </div>
      </div>

      {client && (
        <>
          <section>
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-heading text-lg font-medium">
                {client.name} · {dateStr}
              </h2>
              <span className="label-eyebrow">Scheduled doses</span>
            </div>
            <MARGrid clientId={clientId} dateStr={dateStr} readOnly />
          </section>

          <section>
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-heading text-lg font-medium">Last 7 days history</h2>
              <span className="label-eyebrow">{history.length} entries</span>
            </div>

            {loadingHistory ? (
              <div className="flex items-center justify-center py-10 text-clinical-subtext">
                <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading…
              </div>
            ) : history.length === 0 ? (
              <div className="card-surface p-8 text-center text-sm text-clinical-subtext">
                No medication administrations logged in the last 7 days.
              </div>
            ) : (
              <div className="space-y-4">
                {groupedHistoryEntries.map(([day, entries]) => (
                    <div key={day} className="card-surface p-4">
                      <div className="flex items-center gap-2 mb-3">
                        <Calendar className="h-4 w-4 text-clinical-primary" />
                        <h3 className="font-heading font-medium">{day}</h3>
                        <span className="text-xs text-clinical-subtext">
                          ({entries.length} {entries.length === 1 ? "entry" : "entries"})
                        </span>
                      </div>
                      <div className="space-y-2">
                        {entries.map((e) => (
                          <div
                            key={e.id}
                            className="flex flex-wrap items-center gap-3 rounded-lg border border-clinical-border p-2.5"
                            data-testid={`mar-history-${e.id}`}
                          >
                            <div className="font-mono text-xs text-clinical-subtext w-12 shrink-0">
                              {e.scheduled_time}
                            </div>
                            <div className="min-w-0 flex-1">
                              <div className="text-sm font-medium truncate">
                                {e.medication_name}{" "}
                                <span className="font-normal text-clinical-subtext">
                                  · {e.medication_dosage}
                                </span>
                              </div>
                              <div className="text-xs text-clinical-subtext">
                                by {e.caregiver_name}
                                {e.notes ? ` · ${e.notes}` : ""}
                              </div>
                            </div>
                            <StatusPill status={e.status} />
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
              </div>
            )}
          </section>
        </>
      )}
    </div>
  );
}
